import type { ReactNode } from 'react';
import { t, type Lang } from '../core/index.js';
import { Button, Card, CardHeader, Gauge, Icon, TopBar } from './components/index.js';

/** Greeting with the first name when the account has one. */
export function greeting(lang: Lang, name: string, email: string): string {
  const first = name.trim().split(/\s+/)[0] ?? '';
  // accounts from an e-mail code carry the address (or its local part) as name: no real first name
  const generated = first === email || first === email.split('@')[0];
  return first && !generated ? t(lang, 'hello', { name: first }) : t(lang, 'welcome');
}

/**
 * First run without a machine (board "Erster Start: noch keine Maschine"): the greeting as the title, the empty state in
 * a card (an empty ring with a plus, one title, one line, "Maschine koppeln"), how it goes as a card of steps with a line
 * between them, the hint for an invitation under it.
 */
export function FirstRun({
  lang,
  name,
  email,
  accountButton,
  onPair,
}: {
  lang: Lang;
  name: string;
  email: string;
  accountButton: ReactNode;
  onPair: () => void;
}) {
  const steps = ['firstRunStep1', 'firstRunStep2', 'firstRunStep3'] as const;
  return (
    <section className="screen first-run" aria-labelledby="first-run-title">
      <TopBar title={greeting(lang, name, email)} right={accountButton} />
      <Card className="tab-card first-run-card">
        <div className="first-run-art">
          <Gauge angle={-135} tone="idle">
            <Icon name="plus" size="lg" className="first-run-plus" />
          </Gauge>
        </div>
        <h2 id="first-run-title" className="card-title">
          {t(lang, 'noMachines')}
        </h2>
        <p className="hint">{t(lang, 'firstRunText')}</p>
        <Button block icon="plus" onClick={onPair} data-testid="pair-open">
          {t(lang, 'pair')}
        </Button>
      </Card>
      <Card className="tab-card rows-card first-run-steps">
        <CardHeader title={t(lang, 'firstRunSteps')} />
        {steps.map((key, i) => (
          <div key={key} className="row step-row">
            <span className="step-num">{i + 1}</span>
            <span className="step-text">{t(lang, key)}</span>
          </div>
        ))}
      </Card>
      <p className="hint first-run-invite">{t(lang, 'inviteHint')}</p>
    </section>
  );
}
